import React from 'react';
import { diffTools } from '../config/diff-tools';

export default function HomePage() {
  const groupedTools = {
    'Core Data Formats': diffTools.filter(t =>
      ['json', 'xml', 'yaml', 'csv'].includes(t.format)
    ),
  };

  const categorizedSlugs = new Set(
    Object.values(groupedTools).flat().map(t => t.slug)
  );

  return (
    <div className="homepage">
      <header className="homepage-header">
        <h1>DiffTools.net</h1>
        <p className="homepage-subtitle">
          Free online diff tools for developers. Compare JSON, YAML, XML, configuration files,
          and more. All processing happens in your browser - your data never leaves your device.
        </p>
      </header>

      <div className="tools-grid">
        {Object.entries(groupedTools).map(([category, tools]) => (
          <section key={category} className="tools-category">
            <h2 className="category-title">
              {category}
              <span className="category-count">
                ({tools.length} {tools.length === 1 ? 'tool' : 'tools'})
              </span>
            </h2>
            <div className="tools-list">
              {tools.map((tool) => (
                <a
                  key={tool.slug}
                  href={`#/${tool.slug}`}
                  className="tool-card"
                >
                  <h3 className="tool-title">{tool.keyword}</h3>
                  <p className="tool-description">{tool.description}</p>
                  <span className="tool-format-badge">
                    {tool.format.toUpperCase()}
                  </span>
                </a>
              ))}
            </div>
          </section>
        ))}
      </div>

      <section className="homepage-features">
        <h2>Why Use DiffTools.net?</h2>
        <div className="features-grid">
          <div className="feature">
            <h3>100% Client-Side Processing</h3>
            <p>
              All comparisons happen in your browser. Your sensitive data never leaves your device,
              making it safe for comparing production configurations and API responses.
            </p>
          </div>
          <div className="feature">
            <h3>No Installation Required</h3>
            <p>
              Access professional diff tools instantly from any device. No downloads, no setup,
              no registration - just paste and compare.
            </p>
          </div>
          <div className="feature">
            <h3>Format-Specific Intelligence</h3>
            <p>
              Each tool understands its format's structure, providing meaningful comparisons
              that highlight changes developers actually care about.
            </p>
          </div>
          <div className="feature">
            <h3>Always Free</h3>
            <p>
              All tools are completely free to use with no limits. Perfect for developers,
              DevOps engineers, and teams of any size.
            </p>
          </div>
        </div>
      </section>

      <style>{`
        .homepage {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem 1rem;
        }

        .homepage-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .homepage-header h1 {
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 1rem;
          color: var(--text-primary);
        }

        .homepage-subtitle {
          font-size: 1.125rem;
          color: var(--text-secondary);
          max-width: 800px;
          margin: 0 auto;
          line-height: 1.6;
        }

        .tools-grid {
          margin-bottom: 3rem;
        }

        .tools-category {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .category-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid var(--border-color);
        }

        .category-count {
          font-size: 0.875rem;
          font-weight: 400;
          color: var(--text-secondary);
          margin-left: 0.5rem;
        }

        .tools-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1rem;
        }

        .tool-card {
          display: block;
          padding: 1rem;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          text-decoration: none;
          transition: all 0.2s;
          background: var(--bg-primary);
        }

        .tool-card:hover {
          border-color: var(--accent-color);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }

        .tool-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 0.5rem;
        }

        .tool-card:hover .tool-title {
          color: var(--accent-color);
        }

        .tool-description {
          font-size: 0.875rem;
          color: var(--text-secondary);
          line-height: 1.5;
          margin-bottom: 0.75rem;
        }

        .tool-format-badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
          font-weight: 500;
          background: var(--bg-tertiary);
          color: var(--text-secondary);
          border-radius: 4px;
        }

        .homepage-features {
          background: var(--bg-secondary);
          padding: 2rem;
          border-radius: 8px;
          border: 1px solid var(--border-color);
        }

        .homepage-features h2 {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 1.5rem;
          text-align: center;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .feature h3 {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 0.5rem;
        }

        .feature p {
          font-size: 0.875rem;
          color: var(--text-secondary);
          line-height: 1.6;
        }

        @media (max-width: 768px) {
          .homepage-header h1 {
            font-size: 2rem;
          }

          .tools-list {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
